import api from '@/api'
import Vue from 'vue';
import permissions from '@/api/permissions'
import _ from 'lodash'

const toEmails = emails => {
  return emails.map(e => {
    if (_.isString(e)) {
      return e;
    }

    return _.get(e, 'email');
  })
};

class LotModel
{
  _findPaymentTypeAndNum(paymentId) {
    let num = paymentId;
    let payType = 'Type1';
    let type = 'contract';

    if (num > this.datePointContract) {
      num -= this.datePointContract;
      payType = 'Type2';
      type = 'delivery';

      if (num > this.datePointDelivery) {
        num -= this.datePointDelivery;
        payType = 'Type3';
        type = 'accept';
      }
    }

    return {
      num,
      payType,
      type
    }
  }

  _createPayment(partId, paymentId) {
    const payment = {
      ...this._findPaymentTypeAndNum(paymentId),
      paymentId,
      part: partId,
      days: {
        value: api.Number.fromAPI(),
        precision: new api.Precision()
      },
      percent: {
        value: api.Number.fromAPI(),
        precision: new api.Precision()
      }
    };

    return payment;
  };

  _createPart(partId) {
    const payments = Array.from({length: this.numberOfPayments}, (v, i) => this._createPayment(partId, i + 1));

    const part = {
      partId: partId,
      payments,
      deliveryDate: {
        value: api.Date.fromAPI(),
        precision: new api.Precision()
      },
      receiveDate: {
        value: api.Date.fromAPI(),
        precision: new api.Precision()
      },
      paymentDays: {
        value: api.paymentDays,
        precision: new api.Precision()
      }
    };

    return part;
  }

  static fromAPI(data) {
    const model = new LotModel();

    Object.assign(model, {
      id: data.id,
      hasPublic: !!data.hasPublic,
      state: new api.LotState(data.state),
      isFilled: !!data.filled,
      reason: data.declineReason,
      cancelReason: data.cancelReason,
      changeDescription: data.changeDescription,
      creationTime: data.time,
      modifiedTime: data.approveTime,
      datePoints: [],
      serviceStartDate: api.Date.fromAPI(data.startService),
      owner: {
        firstName: data.firstName,
        lastName: data.lastName,
        middleName: data.middleName,
        department: data.department ? data.department.name : undefined,
      },
      threshold: {
        isPercent: !data.edgeMoney,
        unit: data.edgeMoney ? 'money' : 'percent',
        value: +data.edgeValue
      },
      tenderId: data.tenderNum,
      identifier: data.lotNum,
      catalog: {
        name: data.lotNum,
        id: data.lotId,
      },
      methods: {
        planned: new api.TenderMethod(data.planMethod),
        actual: new api.TenderMethod(data.factMethod),
      },
      name: data.name,
      description: data.description,
      additionalInfo: data.additionalInfo,
      withTaxes: data.nds,
      department: data.initialDepartment,
      price: +(data.initialPrice || 0),
      currency: new api.Currency(data.currency),
      participationMode: new api.TenderParticipationMode(data.open ? api.TenderParticipationMode.OPEN : api.TenderParticipationMode.CLOSED),
      documents: {
        docs: data.documents.filter(doc => doc.type === 'Document'),
        details: data.documents.filter(doc => doc.type === 'Detail')
      },
      specs: (data.specs || []).map(e => ({
        catalog: {
          id: e.nomenalture,
          name: e.name
        },
        checkDate: !!e.checkDate,
        okei: e.okei,
        okeiName: e.okeiName,
        count: e.count,
        price: e.price,
        parts: e.parts,
        percent: e.percent,
        bidCurrency: (e.bidCurrency || []).map(item => new api.Currency(item)),
        payCurrency: (e.payCurrency || []).map(item => new api.Currency(item)),
        currencyClause: new api.CurrencyClauseOptional(e.currencyClause),

        position: e.position
      })),
      categories: data.categories,
      participants: {
        auto: data.inviteAuto,
        manual: data.inviteManual
      },
      numberOfParts: data.countPart || 0,
      numberOfPayments: data.countPay || 0,
      permissions: permissions.init(data.permissions)
    });

    model.dates = (data.dates && data.dates.length > 0) ? data.dates.map(d => d ? new Date(d) : undefined) : Array.from({length: model.numberOfParts}).fill(0);
    model.checkDates = (data.checkDates && data.checkDates.length > 0) ? data.checkDates.map(d => !!d) : Array.from({length: model.numberOfParts}).fill(false);

    model.datePointContract = api.Number.fromAPI(data.datePointContract);
    model.datePointDelivery = api.Number.fromAPI(data.datePointDelivery);
    model.datePointReceive = api.Number.fromAPI(data.datePointReceive);

    // fill parts
    model.parts = Array.from({length: model.numberOfParts}, (v, i) => ({ partId: i + 1})).map(part => {
      const obj = _.find(data.payments, { partNum: part.partId }) || {};

      const payments = Array.from({length: model.numberOfPayments}, (v, i) => i + 1).map(paymentId => {
        const payment = model._createPayment(part.partId, paymentId);
        const pay = _.find(data.paymentPartsDo, {
          payType: payment.payType,
          num: payment.num,
          part: part.partId}
        ) || {};

        return {
          ...payment,
          days: {
            isFilled: pay.days || pay.daysExactly,
            value: api.Number.fromAPI(pay.days, 0),
            precision: new api.Precision(!!pay.daysExactly),
          },
          percent: {
            isFilled: pay.prc || pay.prcExactly,
            value: api.Number.fromAPI(pay.prc, 0),
            precision: new api.Precision(!!pay.prcExactly),
          }
        }
      });

      const lastPayment = _.maxBy(payments, p => {
        let value = p.num;

        if (p.type === 'delivery') {
          value += 100000;
        }
        else if (p.type === 'accept') {
          value += 200000;
        }

        return value;
      }) || {};

      payments.map(e => {
        e.lastPayment = _.pick(lastPayment, ['num', 'type']);
      });

      return {
        ...part,
        payments,
        deliveryDate: {
          value: api.Date.fromAPI(obj.deliveryDate),
          precision: new api.Precision(!!obj.deliveryDateExactly)
        },
        receiveDate: {
          value: api.Date.fromAPI(obj.receiveDate),
          precision: new api.Precision(!!obj.receiveDateExactly)
        },
        paymentDays: {
          value: api.Number.fromAPI(obj.deliveryDays, 0),
          precision: new api.Precision(!! obj.deliveryDaysExactly)
        }
      };
    });

    const deliveryTypes = data.deliveryTypes || [];
    const paymentTypes = data.paymentTypes || [];

    model.schedule = {
      deliveryTypes: deliveryTypes.map(r => ({
        startDate: api.Date.fromAPI(r[2]),
        fromPart: api.Number.fromAPI(r[0]),
        toPart: api.Number.fromAPI(r[1])
      })),
      paymentTypes: paymentTypes.map(r => ({
        startDate: api.Date.fromAPI(r[2]),
        fromPart: api.Number.fromAPI(r[0]),
        toPart: api.Number.fromAPI(r[1])
      }))
    };

    const dates = model.dates;
    const checkDates = model.checkDates;

    model.deliverySchedule = Array.from({length: model.numberOfParts}, (v, i) => {
      const item = {
        part: i + 1,
        flag: checkDates[i],
        date: dates[i]
      };

      return item;
    });

    model.applyRules(model.schedule, { fromServer: true });

    return model;
  }

  applyRules(rules, opts = {}) {
    _.extend(this.schedule, rules);

    for (const rule of this.schedule.deliveryTypes) {
      let startDate = rule.startDate;
      const dayOfMonth = startDate.getDate();

      for (let part = rule.fromPart; part <= rule.toPart; ++part) {
        this.deliverySchedule[part - 1].minDeliveryDate = startDate;

        let day = 31;
        let month = startDate.getMonth();

        if (dayOfMonth !== 1 && part == rule.toPart) {
          day = dayOfMonth - 1;
        }

        let finishDate = startDate;
        do {
          finishDate = new Date(startDate.getFullYear(), month, day);
          day--;
        } while (finishDate.getMonth() > month);

        if (!opts.fromServer) {
          this.checkDates[part - 1] = true;
          this.dates[part - 1] = finishDate;
          this.parts[part - 1].paymentDays.value = 0;
          this.parts[part - 1].paymentDays.precision = new api.Precision(api.Precision.NOT_WORSE);
        }

        startDate = new Date(finishDate.getFullYear(), finishDate.getMonth() + 1, 1);
      }
    }

    for (const rule of this.schedule.paymentTypes) {
      let date = rule.startDate;
      this.deliverySchedule[rule.fromPart - 1].minPaymentDate = date;

      // for (let part = rule.fromPart; part <= rule.toPart; ++part) {
      //   _.filter(this.parts[part - 1].payments, {type: 'contract'}).map(p => {
      //     p.isBlocked = true;
      //
      //     if (!opts.fromServer) {
      //       p.days.value = 0;
      //       p.days.precision = new api.Precision(api.Precision.NOT_WORSE);
      //       p.percent.value = 0;
      //       p.percent.precision = new api.Precision(api.Precision.NOT_WORSE);
      //     }
      //   });
      // }

      if (!opts.fromServer) {
        const startPart = rule.fromPart;

        for (let part = rule.fromPart + 1; part <= rule.toPart; ++part) {
          for (let paymentIdx = 0; paymentIdx < this.parts[part - 1].payments.length; paymentIdx++) {
            this.parts[part - 1].payments[paymentIdx].days.value = this.parts[startPart - 1].payments[paymentIdx].days.value;
            this.parts[part - 1].payments[paymentIdx].days.precision = this.parts[startPart - 1].payments[paymentIdx].days.precision;
            this.parts[part - 1].payments[paymentIdx].percent.value = this.parts[startPart - 1].payments[paymentIdx].percent.value;
            this.parts[part - 1].payments[paymentIdx].percent.precision = this.parts[startPart - 1].payments[paymentIdx].percent.precision;
          }
        }
      }
    }
  }

  cleanup() {
    if (this.currency.valueOf() === api.Currency.EMPTY) {
      for (let i = 0; i < this.specs.length; ++i) {
        this.specs[i].price = 0;
      }
    }

    for (let i = 0; i < this.checkDates.length; ++i) {
      if (this.checkDates[i] && this.dates[i]) {
        this.parts[i].paymentDays.value = 0;
        this.parts[i].paymentDays.precision = new api.Precision(api.Precision.NOT_WORSE);
      }
    }
  }

  recalc() {
    // update parts in spec in according to new numberOfParts values
    for (let i = 0; i < this.specs.length; ++i) {
      if (!this.specs[i].parts) {
        this.specs[i].parts = [];
      }

      // if number of parts is decreased, then just remove the difference
      if (this.specs[i].parts.length > this.numberOfParts) {
        this.specs[i].parts.splice(this.numberOfParts, this.specs[i].parts.length - this.numberOfParts);
      }
      else if (this.specs[i].parts.length < this.numberOfParts) {
        // fill new part in spec with 0
        this.specs[i].parts = this.specs[i].parts.concat(new Array( this.numberOfParts - this.specs[i].parts.length).fill(0));
      }
    }

    // remove all out-of-range rules
    this.schedule.deliveryTypes = _.filter(this.schedule.deliveryTypes, e => e.toPart <= this.numberOfParts);
    this.schedule.paymentTypes = _.filter(this.schedule.paymentTypes, e => e.toPart <= this.numberOfParts);

    if (this.checkDates.length > this.numberOfParts) {
      // if number of parts is decreased, then just remove the difference
      this.checkDates.splice(this.numberOfParts, this.checkDates.length - this.numberOfParts);
      this.dates.splice(this.numberOfParts, this.dates.length - this.numberOfParts);
    }
    else if (this.checkDates.length < this.numberOfParts) {
      // if number of parts is increased, then add empty dates and checkDates to the end
      this.checkDates = this.checkDates.concat(new Array(this.numberOfParts - this.checkDates.length).fill(false));
      this.dates = this.dates.concat(new Array(this.numberOfParts - this.dates.length).fill(0));
    }

    // update parts in spec in according to new numberOfParts & numberOfPayments values
    // if number of parts is decreased, then just remove the difference
    if (this.parts.length > this.numberOfParts) {
      this.parts.splice(this.numberOfParts, this.parts.length - this.numberOfParts);
    }
    else if (this.parts.length < this.numberOfParts) {
      const newParts = Array.from({
        length: this.numberOfParts - this.parts.length
      }, (v, i) => this._createPart(this.parts.length + i + 1));
      this.parts = this.parts.concat(newParts);
    }

    // we should resize payments array in every part and recalc payment type
    for (const part of this.parts) {
      if (part.payments.length > this.numberOfPayments) {
        part.payments.splice(this.numberOfPayments, part.payments.length - this.numberOfPayments);
      }
      else if (part.payments.length < this.numberOfPayments) {
        const newPayments = Array.from({
          length: this.numberOfPayments - part.payments.length
        }, (v, i) => this._createPayment(part.partId, part.payments.length + i + 1));
        part.payments = part.payments.concat(newPayments);
      }

      for (const payment of part.payments) {
        _.extend(payment, this._findPaymentTypeAndNum(payment.paymentId));

        if (this. numberOfPayments === 1) {
          payment.percent = {
            value: 10000,
            precision: new api.Precision(api.Precision.EXACTLY)
          }
        }
      }
    }
  }

  toAPI() {
    let isFull = true;

    this.cleanup();

    const total = this.specs.reduce((acc, spec) => {
      if (!spec.price) {
        isFull = false;
      }
      acc += (spec.price * spec.count) || 0;
      return acc;
    }, 0);

    isFull = isFull && total;

    if (isFull) {
      this.price = total;
    }

    const json = {
      lotNum: this.catalog.name || undefined,
      lotId: this.catalog.id || undefined,
      planMethod: this.methods.planned ? this.methods.planned.valueOf() : undefined,
      name: this.name || undefined,
      description: this.description || undefined,
      changeDescription: this.changeDescription || undefined,
      additionalInfo: this.additionalInfo || undefined,
      initialDepartment: _.get(this.department, 'id'),
      initialPrice: this.price,
      currency: this.currency.valueOf(),
      inviteAuto: toEmails(this.participants.auto),
      inviteManual: toEmails(this.participants.manual),
      categories: (this.categories || []).map(e => e.id),
      open: this.participationMode == api.TenderParticipationMode.OPEN,
      countPart: this.numberOfParts,
      countPay: this.numberOfPayments,
      startService: api.Date.toAPI(this.serviceStartDate),
      specs: this.specs.map(e => ({
        nomenclature: e.catalog ? e.catalog.id : undefined,
        count: e.count,
        okei: e.okei || undefined,
        price: e.price,
        parts: e.parts,
        percent: !!e.percent,
        bidCurrency: e.bidCurrency.map(item => api.Enum.toAPI(item)),
        payCurrency: e.payCurrency.map(item => api.Enum.toAPI(item)),
        currencyClause: api.Enum.toAPI(e.currencyClause)
      })),

      payments: this.parts.map(p => ({
        partNum: p.partId,
        deliveryDate: api.Date.toAPI(p.deliveryDate.value),
        deliveryDateExactly: p.deliveryDate.precision.valueOf(),
        receiveDate: api.Date.toAPI(p.deliveryDate.value), // currency receiveDate always equals to deliveryDate
        receiveDateExactly: p.receiveDate.precision.valueOf(),
        deliveryDays: api.Number.toAPI(p.paymentDays.value),
        deliveryDaysExactly: p.paymentDays.precision.valueOf(),
      })),

      paymentPartsDo: this.parts.reduce((acc, p) => {
        p.payments.reduce((items, e) => {
          // skip empty records
          if (!e.days.value && !e.days.precision.valueOf() &&
              !e.percent.value && !e.percent.precision.valueOf()) return items;

          items.push({
            part: p.partId,
            payType: e.payType,
            num: e.num,
            days: api.Number.toAPI(e.days.value),
            daysExactly: e.days.precision.valueOf(),
            prc: api.Number.toAPI(e.percent.value),
            prcExactly: e.percent.precision.valueOf()
          });

          return items;
        }, acc);

        return acc;
      }, []),

      nds: this.withTaxes,
      edgeMoney: this.threshold.unit === 'money',
      edgeValue: this.threshold.value,
      datePointContract: api.Number.toAPI(this.datePointContract),
      datePointDelivery: api.Number.toAPI(this.datePointDelivery),
      datePointReceive: api.Number.toAPI(this.datePointReceive),
      checkDates: this.checkDates,
      dates: this.dates.map((e, i) =>  this.checkDates[i] && e ? +e : 0),
      waitingApproval: !!this.waitingApproval,

      deliveryTypes: this.schedule.deliveryTypes.map(e => [api.Number.toAPI(e.fromPart), api.Number.toAPI(e.toPart), api.Date.toAPI(e.startDate, 0)]),
      paymentTypes: this.schedule.paymentTypes.map(e => [api.Number.toAPI(e.fromPart), api.Number.toAPI(e.toPart), api.Date.toAPI(e.startDate, 0)])
    };

    //орма формирования лота. Даты поставок добавить правила при указании соответствующих комбинаций:
    // 1. Нет галки в чекбоксе дата.
    //     Правило: Участник в предложении указывает срок поставки в календарных днях от даты заключения договора.
    //     А организатор в при формировании переторжки устанавливает общие для всех предложений даты поставки и дату приемки по качеству.
    //     При этом Дата поставки не должна быть ранее даты, вычесленной по формуле:
    //       Дата переторжки+ КД на заключение договора + КД поставки из худшего по этому значению предложения
    // 2. Есть только галка в чекбоксе дата.
    //     Правило: Участник в предложении указывает срок поставки в календарных днях от даты заключения договора, дату поставки и дату приёмки по качеству.
    // 3. Есть галка в чекбоксе дата. и орг указал даты поставки и дату приемки по качеству.
    //     Правило:Участник в предложении не указывает ничего.
    //     Срок поставки в календарных днях не рассчитывается.
    //     Дата поставки и дата приёмки по качеству равны указанной дате.

    // we should cleanup payments field depending on checkDates and date
    json.payments.forEach(p => {
      if (json.dates[p.partNum - 1]) {
        p.deliveryDays = 0;
        p.deliveryDaysExactly = false;
        p.deliveryDate = 0;
        p.deliveryDateExactly = false;
        p.receiveDate = 0;
        p.receiveDateExactly = false;
      }
      else {
        p.deliveryDate = 0;
        p.deliveryDateExactly = false;
        p.receiveDate = 0;
        p.receiveDateExactly = false;
      }

      // if (!json.checkDates[p.partNum - 1] && !json.dates[p.partNum - 1]) {
      //   // if checkDate and date are not set, user can only set deliveryDays & deliveryDaysExactly,
      //   //    so we should send undefined for others
      //   p.deliveryDate = 0;
      //   p.deliveryDateExactly = false;
      //   p.receiveDate = 0;
      //   p.receiveDateExactly = false;
      // }
      // else if (json.checkDates[p.partNum - 1] && !json.dates[p.partNum - 1]) {
      //   // if checkDate is set but date is not,
      //   //     user can set all fields but
      //   //      if he set paymentDays, he can't change others
      //   if (p.deliveryDays || p.deliveryDaysExactly) {
      //     p.deliveryDate = 0;
      //     p.deliveryDateExactly = false;
      //     p.receiveDate = 0;
      //     p.receiveDateExactly = false;
      //   }
      // }
      // else if (json.checkDates[p.partNum - 1] && json.dates[p.partNum - 1]) {
      //   // if checkDate and date are set, user can change nothing
      //   p.deliveryDate = 0;
      //   p.deliveryDateExactly = false;
      //   p.receiveDate = 0;
      //   p.receiveDateExactly = false;
      //   p.deliveryDays = 0;
      //   p.deliveryDaysExactly = false;
      // }
    });

    return json;
  }
}

export default {
  state: {
    lots: {},

    publicLots: {}
  },

  getters: {
    lot: s => lotId => s.lots[lotId]
  },

  mutations: {
    setLot(s, data) {
      const model = LotModel.fromAPI(data);

      Vue.set(s.lots, model.id, model);
    },

    saveLot(s, lot) {
      Vue.set(s.lots, lot.id, _.cloneDeep(lot));
    }
  },

  actions: {
    handleLot({commit}, data) {
      commit('setLot', data);
    },

    async createLot() {
      try {
        const response = await api.post(`/lots`);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          return result;
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        console.error('Something went wrong while creating new lot', e);
        throw e;
      }
    },

    async copyLot({}, { id }) {
      try {
        const response = await api.post(`lots/${id}/copy`);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          return result;
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        console.error('Something went wrong while copying a lot', e);
        throw e;
      }
    },

    async removeLot({}, { id }) {
      let error;

      try {
        const response = await api.delete(`lots/${id}`);
        const { status, errorCode } = response.data;

        if (status !== 200) {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        console.error('Something went wrong while copying a lot', e);
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async fetchLot({dispatch, getters}, {lotId}) {
      try {
        const response = await api.get(`/lots/${lotId}`);
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          await dispatch('handleLot', result);
          return getters.lot(lotId);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        console.error('Something went wrong while creating new lot', e);
        throw e;
      }
    },

    async updateLot({dispatch, getters}, lot) {
      try {

        const response = await api.put(`/lots/${lot.id}`, lot.toAPI());
        const { status, result, errorCode } = response.data;

        if (status === 200) {
          dispatch('handleLot', result);
          return getters.lot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        console.error('Something went wrong while creating new lot', e);
        throw e;
      }
    },

    async addDocumentToLot({dispatch, getters}, {lotId, sign, file, isDocument}) {
      let error;

      try {
        const data = new FormData();
        data.append('sign', sign);
        data.append('file', file);

        const response = await api.post(`lots/${lotId}/attach/${isDocument ? 'Document' : 'Detail'}`, data);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handleLot', result);
          return getters.lot(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async removeDocumentFromLot({dispatch, getters}, {lotId, docId, isDocument}) {
      try {
        const response = await api.delete(`lots/${lotId}/${docId}/attach/${isDocument ? 'Document' : 'Detail'}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          dispatch('handleLot', result);
          return getters.lot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async getLotSigningText({}, {id} ) {
      let error;

      try {
        const response = await api.get(`lots/${id}/sign`);
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          return result;
        }
        error = new api.Errors(errorCode);
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async publishLot({dispatch, getters}, { id, text, signature }) {
      let error;

      try {
        const response = await api.put(`lots/${id}/commit`, {
          dataForSign: text,
          sign: signature
        });
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          dispatch('handleLot', result);
          return getters.lot(result.id);
        }
        else {
          error = new api.Errors(errorCode)
        }
      }
      catch (e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async createTenderForLot({}, { id }) {
      try {
        const response = await api.put(`lots/${id}/tender`);
        const {status, result} = response.data;

        if (status === 200) {
          return result.id;
        }
      }
      catch(e) {

      }
    },

    async acceptLot({dispatch, getters}, { id }) {
      try {
        const response = await api.post(`/lots/${id}/approved`, { accept: true });
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          dispatch('handleLot', result);
          return getters.lot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {
        throw e;
      }
    },

    async declineLot({dispatch, getters}, { id, reason }) {
      try {
        const response = await api.post(`/lots/${id}/approved`, { accept: false, reason });
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          dispatch('handleLot', result);
          return getters.lot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {

        throw e;
      }
    },

    async declineApprovedLot({dispatch, getters}, { id, reason }) {
      try {
        const response = await api.post(`/lots/${id}/declined`, { reason });
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          dispatch('handleLot', result);
          return getters.lot(result.id);
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch(e) {

        throw e;
      }
    },

    async uploadSpecificationAndPayments({dispatch, getters}, {id, file}) {
      let error;

      try {
        const data = new FormData();
        data.append('file', file);

        const response = await api.post(`/lots/${id}/import/specification`, data);
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          dispatch('handleLot', result);
          return getters.lot(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async uploadSpecificationTemplate({}, { id, file }) {
      let error;

      try {
        const data = new FormData();
        data.append('file', file);

        const response = await api.post(`/lots/${id}/import/specification`, data);
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          const columns = Array.from({ length: result.columnCount }, (v, k) => {
            const p = [];
            do {
              p.push(String.fromCharCode(65 + k % 25));
              k = Math.trunc(k / 25);
            } while (k != 0);

            return p.reverse().join('');
          });

          const fields = [
            'nomenclature',
            'count',
            'price',
            'part'
          ];

          return {
            fileId: result.uid,
            fields: result.data.map((e, i) => ({
              name: new api.SpecificationTemplateField(fields[i]),
              column: e > 0 ? columns[e - 1]: undefined
            })),
            columns
          }
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async completeSpecificationTemplateUpload({dispatch, getters}, { id, fileId, fields }) {
      let error;

      try {
        const data = { file: fileId };

        const response = await api.post(`/lots/${id}/import/specification/complete`, fields.reduce((acc, e) => {
          acc[e.name.valueOf()] = e.column;
          return acc;
        }, data));
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          dispatch('handleLot', result);
          return getters.lot(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async downloadRequestTemplate({}, {id, filename})
    {
      let error;

      try {
        await api.download(`/lots/${id}/requestTemplate`, {}, {
          filename: filename + '.xlsx'
        });
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async downloadSpecificationTemplate({}, { id, filename }) {
      let error;

      try {
        await api.download(`/lots/${id}/specificationTemplate`, {}, {
          filename: filename + '.xlsx'
        });
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async downloadSpecification({}, { id, filename }) {
      let error;

      try {
        await api.download(`/lots/${id}/specification/export`, {}, {
          filename: filename + '.xlsx'
        });
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async checkLotFileSignature({}, {lotId, fileId}) {
      try {
        const response = await api.get(`lots/${lotId}/info/sign/${fileId}`);
        const {status, result, errorCode} = response.data;

        if (status === 200) {
          return {
            data: result.data,
            signature: result.sign,
            certificates: result.certs.map(e => ({
              ...e,
              time: new Date(e.time)
            }))
          }
        }
        else {
          throw new api.Errors(errorCode);
        }
      }
      catch (e) {
      }
    },

    async cancelLot({dispatch, getters}, {id, cancelReason}) {
      let error;

      try {
        const response = await api.put(`/lots/${id}/cancel`, { reason: cancelReason });
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          dispatch('handleLot', result);
          return getters.lot(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async acceptCancellingLot({dispatch, getters}, {id}) {
      let error;

      try {
        const response = await api.put(`/lots/${id}/cancel/true`);
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          dispatch('handleLot', result);
          return getters.lot(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    },

    async rejectCancellingLot({dispatch, getters}, {id}) {
      let error;

      try {
        const response = await api.put(`/lots/${id}/cancel/false`);
        const {status, errorCode, result} = response.data;

        if (status === 200) {
          dispatch('handleLot', result);
          return getters.lot(result.id);
        }
        else {
          error = new api.Errors(errorCode);
        }
      }
      catch(e) {
        error = e;
      }

      if (error) {
        throw error;
      }
    }
  }
}
